<script>
	/**
	 * [레시피] 탭 — **경품 이벤트**(kind='event') 한 벌의 저작. 바깥(RecipeContext)이 고른 **단계 하나**만 그린다.
	 * 피그마: '비밀오븐 이벤트'(1599:4930)
	 *
	 * ⭐ 경품 이벤트 = **공통 틀 + 당첨 방식**. 단계도 그 순서다:
	 *    ① 기본(기간·배경·타이틀) ② 경품(공통) ③ 당첨 방식(지금은 즉석 뽑기) ④ 팝업(당첨·꽝·안내) ⑤ 당첨 현황
	 *    순위 보상 같은 새 방식은 ③ 안에 자기 칸만 더한다 — ①②④⑤는 그대로다.
	 * ⚠️ 확률·수량·당첨 제한은 **서버만 안다**(런타임으로 안 내려간다 — launcherConfig.normalizeEvent).
	 * ⚠️ 문구 칸의 placeholder 는 전부 `예)` 로 시작한다 — 회색 예시를 '이미 들어간 값'으로 읽은 일이 있었다.
	 */
	import { createEventDispatcher, onMount } from "svelte";
	import { PRConnect } from "@projectroom/ui-lib";
	import { Input, Textarea, Select, Button, Switch, Tabs } from "../../lib/ui";
	import ImagePicker from "../chat-builder/ImagePicker.svelte";
	import ImageSlot from "./ImageSlot.svelte";
	import FieldRow from "./FieldRow.svelte";
	import Callout from "./Callout.svelte";
	import { botAssetFolder } from "../chat-builder/imageAssetApi.js";
	import { EVENT_MODES, newPrize, tokenListOf } from "./launcherSlots.js";

	export let bot;
	/** 경품 이벤트 레시피 한 벌({ id, name, kind:'event', event }). */
	export let recipe;
	/** 지금 보여줄 단계(RecipeContext 의 탭): basic | prizes | mode | popups | report */
	export let step = "basic";
	/** assetId → URL(썸네일용, 저장하지 않는다). RecipeContext 와 같은 그릇을 나눠 쓴다. */
	export let recipeUrls = {};
	export let profileFields = [];
	/** 툴팁이 어느 메인 카드에 뜰지 보려고(저작값 그대로). */
	export let tiles = [];
	export let recipes = [];

	const dispatch = createEventDispatcher();

	$: ev = recipe?.event || {};
	$: draw = ev.draw || {};
	$: prizes = Array.isArray(ev.prizes) ? ev.prizes : [];
	$: mode = ev.mode || "draw";
	$: modeHint = EVENT_MODES.find((m) => m.value === mode)?.hint || "";

	/** event 안 한 칸을 바꾼다. */
	const emitEvent = (nextEvent) => dispatch("change", { ...recipe, event: nextEvent });
	const patchEv = (fields) => emitEvent({ ...ev, ...fields });
	const patchIn = (section, fields) => emitEvent({ ...ev, [section]: { ...(ev[section] || {}), ...fields } });

	/* ── 팝업 하위 탭 — 고른 팝업이 미리보기에 뜬다 ── */
	const POPUP_TABS = [
		{ key: "win", label: "당첨" },
		{ key: "lose", label: "꽝" },
		{ key: "info", label: "안내 (?)" },
	];
	let popupTab = "win";
	// 팝업 단계를 떠나면 처음 칸(당첨)으로 — 바깥이 팝업 단계로 들어올 때 '당첨'을 가리키기 때문이다.
	$: if (step !== "popups") popupTab = "win";

	/**
	 * 프리뷰를 이 이벤트로. `popup` = 띄워 볼 팝업('' = 화면만).
	 * `replay` 는 결과 전 화면과 같은 규약 — "next" 면 저장 뒤 새 설정과 함께 다시 선다.
	 * `state` = 볼 버튼 상태('' 참여 가능 · done 오늘 다 씀 · won 이미 당첨) — 꺼진 버튼 문구를 보면서 쓰려고.
	 */
	const focus = (popup = "", replay = "", state = "") => dispatch("focus", { recipeId: recipe.id, popup, replay, state });
	const setPopupTab = (k) => {
		popupTab = k;
		focus(k, "now");
	};

	/* ── 메인 카드 툴팁 — 미리보기는 **메인 화면**으로 간다(툴팁은 이벤트 화면이 아니라 메인 카드 위에 뜬다) ── */
	const focusHome = () => dispatch("focus", { recipeId: recipe.id, home: true });
	$: tip = ev.tip || {};
	/**
	 * 이 이벤트로 이어지는 메인 카드 — 런타임 eventTip.ts `eventOfTile` 과 **같은 규칙**이다(고치면 둘 다):
	 * 카드(격자·레시피 타일)가 이 이벤트를 바로 열거나, 카드의 레시피 결과 버튼이 이 이벤트를 연다.
	 */
	$: tipTiles = (tiles || []).filter((t) => {
		if (!t || t.area !== "grid" || t.presentation !== "native" || !t.recipeId) return false;
		if (t.recipeId === recipe.id) return true;
		const r = (recipes || []).find((x) => x && x.id === t.recipeId);
		return !!r && r.kind !== "event" && (r.actions || []).some((a) => a && a.kind === "recipe" && a.target === recipe.id);
	});

	/* ── 경품 ── */
	const patchPrize = (id, fields) => patchEv({ prizes: prizes.map((p) => (p.id === id ? { ...p, ...fields } : p)) });
	const addPrize = () => patchEv({ prizes: [...prizes, newPrize()] });
	const removePrize = (id) => {
		// ⚠️ 확률표에서도 지운다 — 남기면 없는 경품의 확률이 합계에 섞여 '꽝 확률'이 엉뚱하게 보인다.
		const rates = { ...(draw.rates || {}) };
		delete rates[id];
		emitEvent({ ...ev, prizes: prizes.filter((p) => p.id !== id), draw: { ...draw, rates } });
	};
	const setRate = (id, v) => {
		const n = Math.max(0, Math.min(100, Number(v) || 0));
		patchIn("draw", { rates: { ...(draw.rates || {}), [id]: n } });
	};
	$: rateTotal = prizes.reduce((sum, p) => sum + (Number(draw.rates?.[p.id]) || 0), 0);
	$: unlimitedPrizes = prizes.filter((p) => p.quantity === "" || p.quantity === null || p.quantity === undefined);

	/* ── 그림 ── */
	let pickerOpen = false;
	/** { slot: 'background'|'title'|'loseImage'|'machine'|'handle'|'capsule'|'prize', index?, prizeId? } */
	let picking = null;
	const openPicker = (slot, extra = {}) => {
		picking = { slot, ...extra };
		pickerOpen = true;
	};
	// ⚠️ 함수(`capsules()`)로 두면 템플릿이 draw 변화를 못 따라가 칸이 안 바뀐다 — 반응형 값으로.
	$: capsuleList = Array.isArray(draw.capsuleAssetIds) ? draw.capsuleAssetIds : [];
	/** 그림을 바꾸면 지금 보는 화면(팝업 포함)을 새 그림으로 다시 띄운다. */
	const refocus = () => focus(step === "popups" ? popupTab : "", "next");
	/**
	 * ⚠️⚠️ `clearImage` 와 **짝**이다 — 넣는 길만 있으면 잘못 넣은 그림을 못 치운다(§5 빌더 함정).
	 */
	const onPick = (e) => {
		if (!picking) return;
		const { assetId, url } = e.detail || {};
		recipeUrls = { ...recipeUrls, [assetId]: url };
		const p = picking;
		picking = null;
		if (p.slot === "background") patchEv({ backgroundAssetId: assetId });
		else if (p.slot === "title") patchEv({ titleAssetId: assetId });
		else if (p.slot === "loseImage") patchIn("lose", { imageAssetId: assetId });
		else if (p.slot === "machine") patchIn("draw", { machineAssetId: assetId });
		else if (p.slot === "handle") patchIn("draw", { handleAssetId: assetId });
		else if (p.slot === "floor") patchIn("draw", { floorAssetId: assetId });
		else if (p.slot === "tipIcon") {
			patchIn("tip", { iconAssetId: assetId });
			return focusHome();
		} else if (p.slot === "capsule") {
			const list = [...capsuleList];
			if (p.index === undefined || p.index >= list.length) list.push(assetId);
			else list[p.index] = assetId;
			patchIn("draw", { capsuleAssetIds: list.slice(0, 8) });
		} else if (p.slot === "prize") patchPrize(p.prizeId, { imageAssetId: assetId });
		refocus();
	};
	const clearImage = (slot, extra = {}) => {
		if (slot === "background") patchEv({ backgroundAssetId: null });
		else if (slot === "title") patchEv({ titleAssetId: null });
		else if (slot === "loseImage") patchIn("lose", { imageAssetId: null });
		else if (slot === "machine") patchIn("draw", { machineAssetId: null });
		else if (slot === "handle") patchIn("draw", { handleAssetId: null });
		else if (slot === "floor") patchIn("draw", { floorAssetId: null });
		else if (slot === "tipIcon") {
			patchIn("tip", { iconAssetId: null });
			return focusHome();
		}
		// 캡슐은 칸을 **빼낸다**(null 로 남기면 빈 캡슐 자리가 생긴다).
		else if (slot === "capsule") patchIn("draw", { capsuleAssetIds: capsuleList.filter((_, i) => i !== extra.index) });
		else if (slot === "prize") patchPrize(extra.prizeId, { imageAssetId: null });
		refocus();
	};

	/* ── 당첨 현황 ── */
	let report = null;
	let reportLoading = false;
	let reportError = "";
	const loadReport = () => {
		reportLoading = true;
		reportError = "";
		PRConnect.send(
			"/api/Bot/getLauncherEventReport",
			{ botId: bot.id, recipeId: recipe.id },
			(data) => {
				reportLoading = false;
				if (data.header.status === "success") report = data.result;
				else reportError = "불러오지 못했습니다. (서버를 재시작했는지, 마이그레이션을 넣었는지 확인해주세요)";
			},
			() => {
				reportLoading = false;
				reportError = "불러오지 못했습니다.";
			},
		);
	};
	onMount(loadReport);
	// 다른 이벤트로 바꾸면 그 이벤트의 현황을 다시 받는다(옛 이벤트 숫자가 남아 보이지 않게).
	let reportFor = "";
	$: if (recipe?.id && recipe.id !== reportFor) {
		if (reportFor) {
			report = null;
			loadReport();
		}
		reportFor = recipe.id;
	}
	$: wonOf = (prizeId) => (report?.prizes || []).find((x) => x.prizeId === prizeId)?.won || 0;
	const fmt = (v) => {
		try {
			return new Date(v).toLocaleString("ko-KR", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
		} catch {
			return String(v || "");
		}
	};
	let codesCopied = false;
	const copyCodes = async () => {
		const lines = (report?.winners || []).map((w) => `${w.code}\t${w.prizeBrand ? w.prizeBrand + " " : ""}${w.prizeName}\t${fmt(w.createdAt)}`);
		try {
			await navigator.clipboard.writeText(lines.join("\n"));
			codesCopied = true;
			setTimeout(() => (codesCopied = false), 1500);
		} catch {
			/* 복사가 막힌 환경 — 표를 보고 옮기면 된다 */
		}
	};

	$: tokens = tokenListOf(profileFields);
	/**
	 * 수령 주소에 당첨 코드 자리가 있나(구글폼 '미리 채워진 링크'). ⚠️ 구글이 복사해 주는 링크는
	 * `{code}` 를 `%7Bcode%7D` 로 인코딩해 둔다 — 둘 다 본다(런타임 EventScreen fillClaimUrl 과 같은 규칙).
	 */
	$: claimPrefilled = /\{code\}|%7Bcode%7D/i.test(ev.win?.claimUrl || "");
	/** 코드를 폼에 채워 주는 **열 수 있는** 주소 — 그러면 런타임이 팝업에 코드·복사를 안 띄운다(EventScreen claimFillsCode). */
	$: claimFillsCode = claimPrefilled && /^https?:\/\//i.test(ev.win?.claimUrl || "");
	$: periodBad = ev.startDate && ev.endDate && ev.endDate < ev.startDate;
</script>

<div class="tw-flex tw-flex-col tw-gap-4">
	{#if step === "basic"}
		<!-- ── ① 기본 ── -->
		<p class="step-desc">이벤트 화면의 맨 위 <strong>타이틀 그림</strong>과 바탕, 참여할 수 있는 기간을 정합니다.</p>
		<div class="slots">
			<ImageSlot label="타이틀 그림" hint="화면 위 · 305×145" shape="wide" url={recipeUrls[ev.titleAssetId] || ""} filled={!!ev.titleAssetId} on:pick={() => openPicker("title")} on:clear={() => clearImage("title")} />
			<ImageSlot label="배경" hint="390×844 · 비우면 런처 배경" shape="tall" empty="런처 배경" url={recipeUrls[ev.backgroundAssetId] || ""} filled={!!ev.backgroundAssetId} on:pick={() => openPicker("background")} on:clear={() => clearImage("background")} />
		</div>
		<!-- ⚠️ 없으면 켜짐(`!== false`) — 이 칸이 생기기 전에 만든 이벤트도 지금처럼 움직인다. -->
		<FieldRow label="타이틀 움직임">
			<div class="tw-flex tw-min-h-10 tw-items-center tw-gap-2">
				<Switch checked={ev.titleMotion !== false} on:click={() => patchEv({ titleMotion: ev.titleMotion === false })} aria-label="타이틀 움직임" />
				<span class="tw-text-xs tw-text-muted-foreground">{ev.titleMotion === false ? "멈춤" : "통통 튀며 시소처럼 흔들기"}</span>
			</div>
			<svelte:fragment slot="hint">타이틀 그림이 통통 튀면서 좌우로 살짝 기웁니다. 기간 칩은 움직이지 않습니다. 기기에서 '동작 줄이기'를 켠 사람에게는 멈춰 보입니다.</svelte:fragment>
		</FieldRow>
		<FieldRow label="기간">
			<div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
				<Input class="tw-w-40" type="date" value={ev.startDate || ""} on:change={(e) => patchEv({ startDate: e.target.value })} />
				<span class="tw-text-xs">~</span>
				<Input class="tw-w-40" type="date" value={ev.endDate || ""} on:change={(e) => patchEv({ endDate: e.target.value })} />
			</div>
			<svelte:fragment slot="hint">시작일·종료일 <strong>당일 포함</strong>(한국 시간). 비우면 그쪽 제한이 없습니다. 기간 밖에서는 참여 버튼이 꺼집니다.</svelte:fragment>
		</FieldRow>
		{#if periodBad}<Callout variant="error">종료일이 시작일보다 앞입니다 — 아무도 참여할 수 없습니다.</Callout>{/if}
		<FieldRow label="기간 문구">
			<Input placeholder="예) 이벤트 기한 : 11.02~11.15" value={ev.periodText || ""} on:input={(e) => patchEv({ periodText: e.target.value })} />
			<svelte:fragment slot="hint">타이틀 아래 작은 칩입니다. 비우면 날짜로 만듭니다.</svelte:fragment>
		</FieldRow>

		<!-- ⭐ 메인 카드 위 툴팁(시안 메인화면 1914:25327). 칸을 누르면 미리보기가 메인 화면으로 간다. -->
		<div class="group-title">메인 화면 툴팁 <span class="tw-font-normal tw-text-muted-foreground">— 이 이벤트로 이어지는 메인 카드 위 말풍선</span></div>
		<div class="tip-row">
			<ImageSlot label="아이콘" hint="28×38" empty="기계 그림" url={recipeUrls[tip.iconAssetId] || ""} filled={!!tip.iconAssetId} on:pick={() => openPicker("tipIcon")} on:clear={() => clearImage("tipIcon")} />
			<div class="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
				<Textarea rows={2} placeholder={"예) 행운빵 받고\n캡슐뽑기 도전!"} value={tip.text || ""} on:focus={focusHome} on:input={(e) => patchIn("tip", { text: e.target.value })} />
				<p class="tw-text-xs tw-text-muted-foreground">
					비우면 안 뜹니다. <strong>첫 줄은 크림색</strong>, 둘째 줄부터 흰색입니다. 이벤트 기간에만 보입니다(미리보기는 늘 보임).
					아이콘을 비우면 이 이벤트의 뽑기 기계 그림이 들어갑니다.
				</p>
			</div>
		</div>
		{#if (tip.text || "").trim()}
			{#if tipTiles.length > 0}
				<Callout variant="info">
					<strong>{tipTiles.map((t) => `'${(t.label || "이름 없는 카드").replace(/\n/g, " ")}'`).join(", ")}</strong> 카드 위에 뜹니다.
				</Callout>
			{:else}
				<Callout variant="warn">
					이 이벤트로 이어지는 <strong>메인 카드가 없어</strong> 툴팁이 안 보입니다. [타일]에서 메인 카드가 이 이벤트를 열게 하거나,
					카드가 여는 레시피의 ⑤ 결과 버튼에서 이 이벤트를 고르세요.
				</Callout>
			{/if}
		{/if}
	{:else if step === "prizes"}
		<!-- ── ② 경품 ── -->
		<p class="step-desc">
			어떤 당첨 방식이든 이 목록에서 나갑니다. <strong>수량</strong>이 다 나가면 그 경품은 더 당첨되지 않습니다(꽝 처리).
			{#if mode === "draw"}<strong>확률</strong>은 한 번 누를 때 그 경품이 나올 확률입니다.{/if}
		</p>
		<div class="tw-flex tw-flex-col tw-gap-2">
			{#each prizes as p (p.id)}
				<div class="card-row">
					<ImageSlot label="경품 그림" url={recipeUrls[p.imageAssetId] || ""} filled={!!p.imageAssetId} on:pick={() => openPicker("prize", { prizeId: p.id })} on:clear={() => clearImage("prize", { prizeId: p.id })} />
					<div class="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-2">
						<div class="tw-flex tw-items-center tw-gap-2">
							<Input class="tw-min-w-0 tw-flex-1" placeholder="경품 이름 (예: 아이스 아메리카노 Tall)" value={p.name || ""} on:input={(e) => patchPrize(p.id, { name: e.target.value })} />
							<button type="button" class="del-btn" aria-label="경품 삭제" on:click={() => removePrize(p.id)}>✕</button>
						</div>
						<Input placeholder="브랜드 (예: 스타벅스)" value={p.brand || ""} on:input={(e) => patchPrize(p.id, { brand: e.target.value })} />
						<div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-xs tw-text-muted-foreground">
							<span>수량</span>
							<Input
								class="tw-w-20"
								type="number"
								min="0"
								value={p.quantity ?? ""}
								on:input={(e) => patchPrize(p.id, { quantity: e.target.value === "" ? "" : Math.max(0, Math.floor(Number(e.target.value) || 0)) })}
							/>
							<span>개</span>
							{#if mode === "draw"}
								<span class="tw-ml-3">확률</span>
								<Input class="tw-w-20" type="number" min="0" max="100" step="0.1" value={draw.rates?.[p.id] ?? 0} on:input={(e) => setRate(p.id, e.target.value)} />
								<span>%</span>
							{/if}
							{#if report}
								<span class="tw-ml-auto">나감 <strong>{wonOf(p.id)}</strong>{p.quantity !== "" && p.quantity != null ? ` / ${p.quantity}` : ""}</span>
							{/if}
						</div>
					</div>
				</div>
			{/each}
			<div><Button variant="outline" size="sm" on:click={addPrize}>+ 경품 추가</Button></div>
		</div>
		{#if mode === "draw" && prizes.length > 0}
			<Callout variant={rateTotal > 100 ? "error" : rateTotal === 0 ? "warn" : "info"}>
				당첨 확률 합계 <strong>{Math.round(rateTotal * 100) / 100}%</strong> · 꽝 <strong>{Math.max(0, Math.round((100 - rateTotal) * 100) / 100)}%</strong>
				{#if rateTotal > 100} — 100%를 넘습니다. 넘으면 꽝 없이 <strong>비율로</strong> 나눠 줍니다.{/if}
				{#if rateTotal === 0} — 지금은 <strong>모두 꽝</strong>입니다.{/if}
			</Callout>
		{/if}
		{#if unlimitedPrizes.length > 0}
			<Callout variant="warn">수량을 비운 경품({unlimitedPrizes.map((p) => p.name || "이름 없음").join(", ")})은 <strong>제한 없이</strong> 나갑니다.</Callout>
		{/if}
	{:else if step === "mode"}
		<!-- ── ③ 당첨 방식 ── -->
		<FieldRow label="당첨 방식">
			<Select value={mode} on:change={(e) => patchEv({ mode: e.target.value })}>
				{#each EVENT_MODES as m (m.value)}
					<option value={m.value} disabled={!m.ready}>{m.label}</option>
				{/each}
			</Select>
			<svelte:fragment slot="hint">{modeHint}</svelte:fragment>
		</FieldRow>

		{#if mode === "draw"}
			<div class="group-title">뽑기 기계 <span class="tw-font-normal tw-text-muted-foreground">— 비우면 기본 캡슐 기계</span></div>
			<div class="slots">
				<ImageSlot label="기계" hint="259×384 · 빈 통" shape="tall" empty="기본" url={recipeUrls[draw.machineAssetId] || ""} filled={!!draw.machineAssetId} on:pick={() => openPicker("machine")} on:clear={() => clearImage("machine")} />
				<ImageSlot label="손잡이" hint="67×52 · 뽑을 때 돎" empty={draw.machineAssetId ? "없음" : "기본"} url={recipeUrls[draw.handleAssetId] || ""} filled={!!draw.handleAssetId} on:pick={() => openPicker("handle")} on:clear={() => clearImage("handle")} />
				<!-- 바닥(카펫) — 기계 밑에 깔리고 기계와 같이 줄고 는다(런타임 EventScreen FLOOR · 시안 1914:29240 '카페트'). -->
				<ImageSlot label="바닥" hint="372×128 · 기계 밑 카펫" shape="wide" empty="없음" url={recipeUrls[draw.floorAssetId] || ""} filled={!!draw.floorAssetId} on:pick={() => openPicker("floor")} on:clear={() => clearImage("floor")} />
				{#each capsuleList as id, i (i + ":" + id)}
					<ImageSlot label={`캡슐 ${i + 1}`} url={recipeUrls[id] || ""} filled={true} on:pick={() => openPicker("capsule", { index: i })} on:clear={() => clearImage("capsule", { index: i })} />
				{/each}
				{#if capsuleList.length < 8}
					<ImageSlot label="+ 캡슐" hint={capsuleList.length === 0 ? (draw.machineAssetId ? "비우면 기계째 흔듦" : "비우면 기본 8색") : `${capsuleList.length}/8`} empty="추가" on:pick={() => openPicker("capsule", { index: capsuleList.length })} />
				{/if}
			</div>
			<Callout>
				아무것도 안 올리면 <strong>기본 캡슐 기계</strong>(빨간 기계 · 도는 손잡이 · 8색 캡슐)로 나옵니다. 바꾸고 싶은 것만 올리세요.
				<strong>기계를 직접 올리면</strong> 기본 손잡이·캡슐은 빠집니다(그 그림에 이미 그려져 있을 수 있어서).
				<strong>바닥(카펫)</strong>은 배경에 그리지 말고 여기 따로 올리세요 — 기계는 화면 높이에 따라 크기·자리가 바뀌어서,
				배경에 그려 두면 기계가 카펫 위에 안 앉는 화면이 생깁니다. 여기 올리면 기계를 따라 같이 줄고 늡니다.
			</Callout>

			<div class="group-title">참여 규칙</div>
			<FieldRow label="하루 참여">
				<div class="tw-flex tw-items-center tw-gap-2">
					<Input class="tw-w-20" type="number" min="1" value={draw.dailyLimit ?? 1} on:input={(e) => patchIn("draw", { dailyLimit: Math.max(1, Math.floor(Number(e.target.value) || 1)) })} />
					<span class="tw-text-xs tw-text-muted-foreground">회</span>
				</div>
				<svelte:fragment slot="hint">로그인 안 해도 참여합니다 — 기기 쿠키로 세고, 로그인했으면 계정까지 같이 셉니다.</svelte:fragment>
			</FieldRow>
			<FieldRow label="1인 당첨">
				<div class="tw-flex tw-items-center tw-gap-2">
					<Input class="tw-w-20" type="number" min="0" value={draw.winLimit ?? 1} on:input={(e) => patchIn("draw", { winLimit: Math.max(0, Math.floor(Number(e.target.value) || 0)) })} />
					<span class="tw-text-xs tw-text-muted-foreground">회까지 (기간 전체)</span>
				</div>
				<svelte:fragment slot="hint">0 = 제한 없음. 다 채운 사람은 <strong>더 참여할 수 없습니다</strong> — 버튼이 '당첨 후 버튼' 문구로 꺼집니다(기회도 안 깎임).</svelte:fragment>
			</FieldRow>
			<FieldRow label="하루 당첨 상한">
				<div class="tw-flex tw-items-center tw-gap-2">
					<Input class="tw-w-20" type="number" min="0" value={draw.dailyWinCap ?? 0} on:input={(e) => patchIn("draw", { dailyWinCap: Math.max(0, Math.floor(Number(e.target.value) || 0)) })} />
					<span class="tw-text-xs tw-text-muted-foreground">명 (전체)</span>
				</div>
				<svelte:fragment slot="hint">0 = 없음. 첫날에 경품이 다 나가는 걸 막습니다.</svelte:fragment>
			</FieldRow>

			<div class="group-title">버튼과 연출</div>
			<!-- ⭐ 칸을 누르면 미리보기 버튼이 그 상태로 바뀐다(꺼진 두 상태는 시안 2154:1752 의 꺼진 버튼). -->
			<FieldRow label="버튼">
				<Input placeholder="예) 행운 뽑기" value={draw.buttonLabel || ""} on:focus={() => focus("", "", "")} on:input={(e) => patchIn("draw", { buttonLabel: e.target.value })} />
			</FieldRow>
			<FieldRow label="다 쓴 뒤 버튼">
				<Input placeholder="예) 내일 다시 도전해요!" value={draw.doneLabel || ""} on:focus={() => focus("", "", "done")} on:input={(e) => patchIn("draw", { doneLabel: e.target.value })} />
				<svelte:fragment slot="hint">오늘 참여 횟수를 다 쓰면 꺼진 버튼으로 바뀝니다. 비우면 '내일 다시 도전해요!'</svelte:fragment>
			</FieldRow>
			<FieldRow label="당첨 후 버튼">
				<Input placeholder="예) 이미 당첨되었어요!" value={draw.wonLabel || ""} on:focus={() => focus("", "", "won")} on:input={(e) => patchIn("draw", { wonLabel: e.target.value })} />
				<svelte:fragment slot="hint">'1인 당첨' 횟수를 다 채우면 기간 내내 꺼진 버튼으로 바뀝니다. 비우면 '이미 당첨되었어요!'</svelte:fragment>
			</FieldRow>
			<FieldRow label="들어오는 버튼 밑 글">
				<Input placeholder={"예) 오늘의 뽑기 기회 {n}회"} value={draw.chanceLabel || ""} on:input={(e) => patchIn("draw", { chanceLabel: e.target.value })} />
				<svelte:fragment slot="hint">운세 결과 등 <strong>이 이벤트를 여는 버튼</strong> 밑에 작게 붙습니다. <code>{"{n}"}</code> = 오늘 남은 횟수. 그 버튼도 오늘 다 쓰거나 이미 당첨되면 위 두 문구로 꺼집니다.</svelte:fragment>
			</FieldRow>
			<FieldRow label="흔드는 시간">
				<div class="tw-flex tw-items-center tw-gap-2">
					<Input class="tw-w-24" type="number" min="500" step="100" value={draw.durationMs ?? 3000} on:input={(e) => patchIn("draw", { durationMs: Math.max(500, Number(e.target.value) || 3000) })} />
					<span class="tw-text-xs tw-text-muted-foreground">ms</span>
				</div>
				<svelte:fragment slot="hint">캡슐이 흔들린 뒤 결과 팝업이 뜹니다.</svelte:fragment>
			</FieldRow>
		{/if}
	{:else if step === "popups"}
		<!-- ── ④ 팝업 — 하위 탭으로 하나씩, 고른 팝업이 미리보기에 뜬다 ── -->
		<Tabs items={POPUP_TABS} active={popupTab} on:change={(e) => setPopupTab(e.detail)} />
		{#if popupTab === "win"}
			<p class="step-desc">당첨됐을 때. 경품 카드(② 경품의 그림·브랜드·이름)와 당첨 코드가 함께 나옵니다.</p>
			<FieldRow label="제목">
				<Input placeholder="예) 행운 뽑기에 당첨됐어요!" value={ev.win?.title || ""} on:input={(e) => patchIn("win", { title: e.target.value })} />
			</FieldRow>
			<FieldRow label="설명">
				<Input placeholder="예) 경품 수령을 위해 정보가 필요해요." value={ev.win?.desc || ""} on:input={(e) => patchIn("win", { desc: e.target.value })} />
			</FieldRow>
			<FieldRow label="수령 버튼">
				<Input placeholder="예) 경품 수령 정보 입력하기" value={ev.win?.claimLabel || ""} on:input={(e) => patchIn("win", { claimLabel: e.target.value })} />
			</FieldRow>
			<FieldRow label="구글폼 주소">
				<Input placeholder="https://docs.google.com/forms/…  (미리 채워진 링크 권장)" value={ev.win?.claimUrl || ""} on:input={(e) => patchIn("win", { claimUrl: e.target.value.trim() })} />
				<svelte:fragment slot="hint">
					수령 버튼을 누르면 새 창으로 열립니다.
					{#if claimPrefilled}
						<span class="tw-text-emerald-600">✓ 당첨 코드가 폼에 자동으로 채워집니다.</span>
					{/if}
				</svelte:fragment>
			</FieldRow>
			{#if ev.win?.claimUrl && !claimPrefilled}
				<Callout>
					<strong>당첨 코드를 자동으로 채우려면</strong> 구글폼 편집 화면 ⋮ → <strong>미리 채워진 링크 가져오기</strong> →
					당첨 코드 칸에 <code>{"{code}"}</code> 입력 → 링크 가져오기 → 링크 복사 → 여기 붙여 넣으세요.
					경품 이름 칸이 있으면 <code>{"{prize}"}</code> 도 됩니다. 지금은 당첨자가 코드를 직접 복사해 붙여 넣어야 합니다.
					<br />⚠️ 당첨 코드 칸에 정규식(응답 확인)이 켜져 있으면 <code>{"{code}"}</code> 가 형식 오류로 막힐 수 있어요 — 그때는 잠깐 끄고 링크를 만든 뒤 다시 켜세요.
				</Callout>
			{/if}
			{#if ev.win?.claimUrl && !/^https?:\/\//i.test(ev.win.claimUrl)}
				<Callout variant="error">https:// 로 시작하는 주소여야 열립니다.</Callout>
			{/if}
			<FieldRow label="당첨 코드">
				{#if claimFillsCode}
					<!-- 코드가 폼에 채워지므로 팝업엔 안 띄운다(런타임 EventScreen claimFillsCode) — 스위치가 뜻이 없어 설명으로 바꾼다. -->
					<div class="tw-flex tw-min-h-10 tw-items-center tw-text-xs tw-text-muted-foreground">
						팝업에 안 보입니다 — 구글폼에 자동으로 채워집니다.
					</div>
				{:else}
					<div class="tw-flex tw-min-h-10 tw-items-center tw-gap-2">
						<Switch checked={ev.win?.showCode !== false} on:click={() => patchIn("win", { showCode: ev.win?.showCode === false })} aria-label="당첨 코드 보이기" />
						<span class="tw-text-xs tw-text-muted-foreground">보이기</span>
					</div>
				{/if}
				<svelte:fragment slot="hint">구글폼에 이 코드를 적게 하면 ⑤ 당첨 현황의 코드와 맞춰 진짜 당첨자인지 확인할 수 있습니다.</svelte:fragment>
			</FieldRow>
			<Callout variant="warn">이름·연락처·주소는 <strong>여기서 받지 않습니다</strong>(구글폼이 받습니다). 구글폼 쪽에 개인정보 수집 동의 문구를 꼭 넣어주세요.</Callout>
		{:else if popupTab === "lose"}
			<p class="step-desc">꽝일 때. 버튼을 누르면 홈으로 돌아갑니다.</p>
			<div class="slots">
				<ImageSlot label="꽝 그림" hint="가운데 카드" url={recipeUrls[ev.lose?.imageAssetId] || ""} filled={!!ev.lose?.imageAssetId} on:pick={() => openPicker("loseImage")} on:clear={() => clearImage("loseImage")} />
			</div>
			<FieldRow label="제목">
				<Input placeholder="예) 오늘은 아쉽지만 다음 기회에!" value={ev.lose?.title || ""} on:input={(e) => patchIn("lose", { title: e.target.value })} />
			</FieldRow>
			<FieldRow label="설명">
				<Input placeholder="예) 내일도 행운을 준비해둘게요." value={ev.lose?.desc || ""} on:input={(e) => patchIn("lose", { desc: e.target.value })} />
			</FieldRow>
			<FieldRow label="버튼">
				<Input placeholder="예) 홈으로 가기" value={ev.lose?.buttonLabel || ""} on:input={(e) => patchIn("lose", { buttonLabel: e.target.value })} />
			</FieldRow>
		{:else}
			<p class="step-desc">화면 오른쪽 위 (?) 를 누르면 나오는 안내입니다. <strong>내용을 비우면 (?) 버튼이 사라집니다.</strong></p>
			<FieldRow label="제목">
				<Input placeholder="예) 이벤트 안내" value={ev.info?.title || ""} on:input={(e) => patchIn("info", { title: e.target.value })} />
			</FieldRow>
			<FieldRow label="내용" top>
				<Textarea
					rows="5"
					placeholder={"예) 하루 1회 참여할 수 있어요.\n당첨 결과는 바로 확인할 수 있어요.\n이벤트 기간 중 1인 1회만 당첨될 수 있어요."}
					value={ev.info?.text || ""}
					on:input={(e) => patchIn("info", { text: e.target.value })}
				/>
				<svelte:fragment slot="hint">줄마다 한 항목(점 목록)으로 나옵니다.</svelte:fragment>
			</FieldRow>
			<FieldRow label="주의 문구">
				<Input placeholder="예) ※ 부정 참여가 확인되면 당첨이 취소될 수 있어요." value={ev.info?.note || ""} on:input={(e) => patchIn("info", { note: e.target.value })} />
			</FieldRow>
			<FieldRow label="버튼">
				<Input placeholder="예) 확인" value={ev.info?.confirmLabel || ""} on:input={(e) => patchIn("info", { confirmLabel: e.target.value })} />
			</FieldRow>
		{/if}
		<p class="tw-text-xs tw-text-muted-foreground">문구에 쓸 수 있는 값: {#each tokens as tk (tk)}<code>{tk}</code> {/each}</p>
	{:else if step === "report"}
		<!-- ── ⑤ 당첨 현황 ── -->
		<div class="tw-flex tw-items-center tw-gap-2">
			<p class="step-desc tw-flex-1">게시본(공개 주소)에서 참여한 기록입니다 — 미리보기에서 누른 건 기록되지 않습니다. 개인정보 없이 <strong>코드·경품·시각</strong>만 있습니다.</p>
			<Button variant="outline" size="sm" on:click={loadReport} disabled={reportLoading}>{reportLoading ? "불러오는 중…" : "새로고침"}</Button>
		</div>
		{#if reportError}
			<Callout variant="error">{reportError}</Callout>
		{:else if report}
			<div class="stats">
				<div><strong>{report.totals.draws}</strong><span>참여(오늘 {report.totals.todayDraws})</span></div>
				<div><strong>{report.totals.devices}</strong><span>기기</span></div>
				<div><strong>{report.totals.wins}</strong><span>당첨</span></div>
			</div>
			{#if prizes.length > 0}
				<div class="tw-flex tw-flex-wrap tw-gap-2 tw-text-xs">
					{#each prizes as p (p.id)}
						<span class="chip">{p.name || "이름 없음"} — 나감 <strong>{wonOf(p.id)}</strong>{p.quantity !== "" && p.quantity != null ? ` / ${p.quantity}` : ""}</span>
					{/each}
				</div>
			{/if}
			{#if report.winners.length > 0}
				<div class="tw-flex tw-justify-end">
					<Button variant="outline" size="sm" on:click={copyCodes}>{codesCopied ? "복사됨" : "목록 복사 (코드·경품·시각)"}</Button>
				</div>
				<div class="tw-max-h-72 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-border">
					<table class="tw-w-full tw-text-xs">
						<thead class="tw-sticky tw-top-0 tw-bg-muted">
							<tr><th class="tw-p-2 tw-text-left">코드</th><th class="tw-p-2 tw-text-left">경품</th><th class="tw-p-2 tw-text-left">시각</th><th class="tw-p-2 tw-text-left">로그인</th></tr>
						</thead>
						<tbody>
							{#each report.winners as w (w.code)}
								<tr class="tw-border-t tw-border-border">
									<td class="tw-p-2 tw-font-mono">{w.code}</td>
									<td class="tw-p-2">{w.prizeBrand ? w.prizeBrand + " " : ""}{w.prizeName}</td>
									<td class="tw-p-2">{fmt(w.createdAt)}</td>
									<td class="tw-p-2">{w.loggedIn ? "예" : "-"}</td>
								</tr>
							{/each}
						</tbody>
					</table>
				</div>
			{:else}
				<Callout>아직 당첨자가 없습니다.</Callout>
			{/if}
		{/if}
	{/if}
</div>

<ImagePicker bind:open={pickerOpen} chatId={bot.id} contextFolder={botAssetFolder(bot)} mode="single" on:select={onPick} />

<style>
	/* RecipeContext 와 같은 모양(단계 설명 · 그림 칸 줄 · 소제목) — 두 편집기가 한 화면처럼 보이게. */
	.step-desc {
		margin: 0;
		font-size: 13px;
		line-height: 1.55;
		color: hsl(var(--sb-muted-foreground));
	}
	/* 이름표 높이 맞춤은 ImageSlot 이 한다(썸네일 자리 높이 고정). */
	.slots {
		display: flex;
		flex-wrap: wrap;
		align-items: flex-start;
		gap: 16px;
		padding: 12px;
		border-radius: 10px;
		background: hsl(var(--sb-muted) / 0.5);
	}
	.tip-row {
		display: flex;
		align-items: flex-start;
		gap: 12px;
	}
	.group-title {
		margin-top: 6px;
		padding-top: 12px;
		border-top: 1px solid hsl(var(--sb-border));
		font-size: 13px;
		font-weight: 600;
		color: hsl(var(--sb-foreground));
	}
	.card-row {
		display: flex;
		align-items: flex-start;
		gap: 12px;
		padding: 10px;
		border-radius: 10px;
		border: 1px solid hsl(var(--sb-border));
	}
	.del-btn {
		flex-shrink: 0;
		width: 28px;
		height: 28px;
		border-radius: 6px;
		border: 1px solid transparent;
		background: transparent;
		color: hsl(var(--sb-destructive));
		font-size: 12px;
		cursor: pointer;
	}
	.del-btn:hover {
		border-color: hsl(var(--sb-destructive) / 0.4);
	}
	.stats {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 8px;
	}
	.stats > div {
		display: flex;
		flex-direction: column;
		align-items: center;
		padding: 10px;
		border-radius: 10px;
		background: hsl(var(--sb-muted));
	}
	.stats strong {
		font-size: 20px;
	}
	.stats span {
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
	}
	.chip {
		padding: 4px 10px;
		border-radius: 999px;
		border: 1px solid hsl(var(--sb-border));
	}
	code {
		font-size: 11px;
		padding: 0 3px;
		border-radius: 3px;
		background: hsl(var(--sb-muted));
	}
</style>
